import { Container, Row, Col, Card, CardBody, Input, Button, Spinner } from 'reactstrap';
import { UserDailyProps, UserDayActivity } from '../../../../types/Dashboard';
import { useEffect, useMemo, useState } from 'react';
import {
  fmtDT,
  fmtTime,
  formatMsAsClock,
  msToMin,
  palette,
  todayYMD,
} from '../../../../lib/dashboardUtils';
import { getUserDayActivity } from '../../../../services/api';
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
const UserDailyActivity: React.FC<UserDailyProps> = ({ userId, initialDate }) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<string>(initialDate || todayYMD());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UserDayActivity | null>(null);

  const load = async (d: string) => {
    setLoading(true);
    try {
      const resp = await getUserDayActivity(userId, d);
      setData(resp);
    } catch (e) {
      console.error('getUserDayActivity', e);
      setData(null);
    } finally {
      setLoading(false);
      // nudge Recharts
      setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    }
  };

  useEffect(() => {
    if (!userId) return;
    load(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, date]);

  // Top 10 lessons by watch time (ms from rollup)
  const watchByLesson = useMemo(() => {
    const rows =
      (data?.per_lesson ?? []).map((r) => {
        const ms = Number(r.watch_ms ?? 0);
        return {
          lesson: r.lesson || r.lesson_id || '—',
          watch_ms: ms,
          watch_clock: formatMsAsClock(ms),
        };
      }) || [];

    rows.sort((a, b) => b.watch_ms - a.watch_ms);
    return rows.slice(0, 10);
  }, [data]);

  // Top 10 by events (opens vs plays(initial))
  const openPlayByLesson = useMemo(
    () =>
      (data?.per_lesson ?? [])
        .map((r) => ({
          lesson: r.lesson || r.lesson_id || '—',
          opens: Number(r.opens) || 0,
          plays: Number(r.plays) || 0,
        }))
        .sort((a, b) => b.plays - a.plays)
        .slice(0, 10),
    [data],
  );

  const name = useMemo(() => {
    const f = data?.user?.firstName ?? '';
    const l = data?.user?.lastName ?? '';
    if (f || l) return `${f} ${l}`.trim();
    return data?.user?.email ?? `User #${userId}`;
  }, [data, userId]);

  return (
    <div className="p-3">
      {/* Header */}
      <Card>
        <CardBody>
          <Row className="align-items-center g-2">
            <Col>
              <h5 className="mb-1">{name}</h5>
              <div style={{ color: '#6b7280' }}>{t('stats.dayActivityFor', { date })}</div>
            </Col>
            <Col md="3">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* KPIs */}
      {loading && !data ? (
        <div className="py-5 text-center">
          <Spinner size="sm" /> Loading…
        </div>
      ) : data ? (
        <>
          <Row className="mt-2 g-3">
            <Col md="3">
              <KPICard
                label={t('stats.lastLogonToday')}
                value={data.last_login_today ? fmtDT(data.last_login_today) : '—'}
              />
            </Col>
            <Col md="3">
              <KPICard label={t('stats.sessionsToday')} value={data.sessions_today} />
            </Col>
            <Col md="2">
              <KPICard label={t('stats.lessonOpens')} value={data.totals.opens} />
            </Col>
            <Col md="2">
              <KPICard label={t('stats.lessonPlays')} value={data.totals.plays} />
            </Col>
            <Col md="2">
              <KPICard label={t('stats.watchTime')} value={fmtTime(data.totals.watch_ms, t)} />
            </Col>
          </Row>

          {/* Watch time by lesson (TOP 10) */}
          <Card className="mt-3">
            <CardBody>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                {t('stats.watchTimeLessonTop10')}
              </div>
              <div style={{ width: '100%', height: 320 }}>
                {watchByLesson.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={watchByLesson}
                      layout="vertical"
                      margin={{ left: 8, right: 16, top: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis
                        type="number"
                        tickFormatter={(v) => formatMsAsClock(Number(v))}
                        domain={[0, 'dataMax']}
                      />
                      <YAxis type="category" dataKey="lesson" width={240} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => [formatMsAsClock(Number(v)), t('stats.watch')]} />
                      <Bar
                        dataKey="watch_ms"
                        name={t('stats.watch')}
                        isAnimationActive={false}
                        fill={palette[2]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: '#9ca3af' }}>{t('stats.noWatchActivity')}</div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Per-lesson summary */}
          <Card className="mt-3">
            <CardBody>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('stats.lessonsToday')}</div>
              {(data.per_lesson ?? []).length ? (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 240 }}>{t('stats.lesson')}</th>
                        <th className="text-end">{t('stats.opens')}</th>
                        <th className="text-end">{t('stats.plays')}</th>
                        <th className="text-end">{t('stats.watch')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.per_lesson.map((r) => (
                        <tr key={r.lesson_id ?? `${r.lesson}-${Math.random()}`}>
                          <td>{r.lesson || r.lesson_id || '—'}</td>
                          <td className="text-end">{r.opens}</td>
                          <td className="text-end">{r.plays}</td>
                          <td className="text-end">{fmtTime(r.watch_ms, t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: '#9ca3af' }}>{t('stats.noLessonActivity')}</div>
              )}
            </CardBody>
          </Card>

          {/* Opens vs Plays (events) – top 10 lessons by plays */}
          <Card className="mt-3">
            <CardBody>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                {t('stats.opensVsPlaysByLessonEventsTop10')}
              </div>
              <div style={{ width: '100%', height: 320 }}>
                {openPlayByLesson.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={openPlayByLesson}
                      margin={{ left: 8, right: 16, top: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis
                        dataKey="lesson"
                        interval={0}
                        angle={-30}
                        textAnchor="end"
                        height={70}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="opens"
                        name={t('stats.opens')}
                        isAnimationActive={false}
                        fill={palette[0]}
                      />
                      <Bar
                        dataKey="plays"
                        name={t('stats.plays')}
                        isAnimationActive={false}
                        fill={palette[1]}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: '#9ca3af' }}>{t('stats.noEvents')}</div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Opens / Plays chronologically */}
          <Row className="mt-3">
            <Col md="6">
              <Card>
                <CardBody>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    {t('stats.opensChronological')}
                  </div>
                  {(data.opens ?? []).length ? (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th style={{ width: 120 }}>{t('stats.eventStatTime')}</th>
                            <th>{t('stats.lesson')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.opens.map((o, i) => (
                            <tr key={i}>
                              <td>{new Date(o.ts).toLocaleTimeString('uk-UA')}</td>
                              <td>{o.lesson || o.lesson_id || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ color: '#9ca3af' }}>{t('stats.noOpens')}</div>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md="6">
              <Card>
                <CardBody>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    {t('stats.playsChronological')}
                  </div>
                  {(data.plays ?? []).length ? (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th style={{ width: 120 }}>{t('stats.eventStatTime')}</th>
                            <th>{t('stats.lesson')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.plays.map((p, i) => (
                            <tr key={i}>
                              <td>{new Date(p.ts).toLocaleTimeString('uk-UA')}</td>
                              <td>{p.lesson || p.lesson_id || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ color: '#9ca3af' }}>{t('stats.noPlays')}</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <div className="py-5 text-center" style={{ color: '#9ca3af' }}>
          {t('stats.noData')}
        </div>
      )}
    </div>
  );
};

export default UserDailyActivity;

// ---- Small presentational KPI card (same as your existing) ----
const KPICard: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Card>
    <CardBody>
      <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
    </CardBody>
  </Card>
);
